import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const phases = [
  {
    n: "01",
    label: "Consult & Design",
    weeks: "Weeks 0—4",
    headline: "The thesis before the machinery.",
    body:
      "Principal interviews, mandate alignment, jurisdictional design, and target operating model. We surface the questions that shape every hire, tool, and reporting line.",
    deliverables: ["Mandate charter", "TOM blueprint", "Governance memo"],
  },
  {
    n: "02",
    label: "Build & Staff",
    weeks: "Weeks 4—10",
    headline: "A working desk in your name.",
    body:
      "Talent sourcing across our regional network, tooling stack (CRM, diligence, portfolio ops), legal entity setup, and secure data room architecture — assembled to production standards.",
    deliverables: ["Lead investors & analysts", "Tooling stack", "Data room + OpSec"],
  },
  {
    n: "03",
    label: "Execute & Operate",
    weeks: "Ongoing",
    headline: "Deals, decisions, deployment.",
    body:
      "Sourcing, diligence, portfolio operations, and reporting — executed by our team, governed by yours. Weekly cadences with your principal, IC-ready memos, and full audit trail.",
    deliverables: ["Deal pipeline", "IC-ready memos", "Portfolio ops"],
  },
  {
    n: "04",
    label: "Measure & Optimize",
    weeks: "Quarterly",
    headline: "ROI, visible and defensible.",
    body:
      "MOIC, IRR, DPI, and impact metrics benchmarked quarterly against the mandate. We iterate the operating model as the mandate evolves — new geographies, new theses, new modules.",
    deliverables: ["Board deck", "IRR/MOIC report", "Optimization plan"],
  },
];

export default function Framework() {
  const [active, setActive] = useState(0);
  const current = phases[active];

  return (
    <section
      id="framework"
      className="relative py-24 md:py-40 border-t border-[var(--line)]"
      data-testid="framework-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              III. Framework
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A four-phase build. <em className="text-gold not-italic">Ninety days</em> to a working desk.
            </h2>
          </div>
          <p className="md:col-span-5 md:col-start-8 md:pt-6 text-ivory/70 leading-relaxed">
            Every capability center we operate is built on the same disciplined
            arc — consult, build, execute, optimize. Predictable milestones. No
            surprises at the board table.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Phase selector */}
          <div className="lg:col-span-5">
            <ul className="space-y-2 md:space-y-3">
              {phases.map((p, i) => (
                <li key={p.n}>
                  <button
                    onClick={() => setActive(i)}
                    data-testid={`framework-phase-${i}`}
                    className={`w-full text-left group flex items-baseline gap-6 py-5 md:py-6 border-t border-[var(--line)] transition-colors ${
                      active === i ? "text-ivory" : "text-ivory/50 hover:text-ivory"
                    }`}
                  >
                    <span
                      className={`chapter-num text-2xl md:text-3xl ${
                        active === i ? "text-gold" : "text-ivory-dim"
                      }`}
                    >
                      {p.n}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-display text-2xl md:text-3xl leading-tight">
                          {p.label}
                        </span>
                        <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                          {p.weeks}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`text-gold transition-opacity ${
                        active === i ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      →
                    </span>
                  </button>
                </li>
              ))}
              <li>
                <div className="border-t border-[var(--line)]" />
              </li>
            </ul>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7 lg:pl-8 xl:pl-16 border-t lg:border-t-0 lg:border-l border-[var(--line)] pt-8 lg:pt-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.n}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
              >
                <div className="text-[10px] tracking-eyebrow uppercase text-gold">
                  Phase {current.n} · {current.weeks}
                </div>
                <h3 className="mt-4 font-display text-3xl md:text-5xl leading-[1.1] tracking-tight text-ivory">
                  {current.headline}
                </h3>
                <p className="mt-6 text-ivory/70 text-base md:text-lg leading-relaxed max-w-2xl">
                  {current.body}
                </p>
                <div className="mt-10">
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-4">
                    Deliverables
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {current.deliverables.map((d) => (
                      <li
                        key={d}
                        className="border border-[var(--line)] p-4 text-sm text-ivory/80"
                      >
                        <span className="text-emerald mr-2">▸</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
